/**
 * Snowfall Effect Component
 * Canvas-based snowfall animation for Christmas and winter themes
 * Adapts color based on light/dark mode for visibility
 */

import { useEffect, useRef } from 'react'

interface SnowfallProps {
  intensity?: 'low' | 'medium' | 'high'
  mode?: 'light' | 'dark'
}

interface Snowflake {
  x: number
  y: number
  radius: number
  speed: number
  opacity: number
}

export function Snowfall({ intensity = 'medium', mode = 'dark' }: SnowfallProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animationFrameRef = useRef<number>()
  const snowflakesRef = useRef<Snowflake[]>([])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resizeCanvas()
    window.addEventListener('resize', resizeCanvas)

    // Intensity settings
    const intensitySettings = {
      low: { count: 30, minRadius: 1, maxRadius: 3, minSpeed: 0.5, maxSpeed: 1.5 },
      medium: { count: 60, minRadius: 1, maxRadius: 4, minSpeed: 1, maxSpeed: 2.5 },
      high: { count: 100, minRadius: 2, maxRadius: 5, minSpeed: 1.5, maxSpeed: 3.5 },
    }

    const settings = intensitySettings[intensity]

    // Initialize snowflakes
    const initSnowflakes = () => {
      snowflakesRef.current = []
      for (let i = 0; i < settings.count; i++) {
        snowflakesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * (settings.maxRadius - settings.minRadius) + settings.minRadius,
          speed: Math.random() * (settings.maxSpeed - settings.minSpeed) + settings.minSpeed,
          opacity: Math.random() * 0.5 + 0.5, // 0.5 to 1.0
        })
      }
    }

    initSnowflakes()

    // Get snow color based on mode
    // Light mode: Use darker colors (gray/blue) for visibility on light backgrounds
    // Dark mode: Use white for visibility on dark backgrounds
    const getSnowColor = (opacity: number): string => {
      if (mode === 'light') {
        // Light mode: Use gray-blue tones (darker) for visibility
        // RGB: 100, 149, 237 (cornflower blue) or 176, 196, 222 (light steel blue)
        return `rgba(100, 149, 237, ${opacity * 0.7})` // Cornflower blue with opacity
      } else {
        // Dark mode: Use white for visibility
        return `rgba(255, 255, 255, ${opacity})`
      }
    }

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      snowflakesRef.current.forEach((flake) => {
        // Update position
        flake.y += flake.speed
        flake.x += Math.sin(flake.y * 0.01) * 0.5 // Slight horizontal drift

        // Reset if off screen
        if (flake.y > canvas.height) {
          flake.y = -10
          flake.x = Math.random() * canvas.width
        }

        // Draw snowflake with mode-appropriate color
        ctx.beginPath()
        ctx.arc(flake.x, flake.y, flake.radius, 0, Math.PI * 2)
        ctx.fillStyle = getSnowColor(flake.opacity)
        ctx.fill()
      })

      animationFrameRef.current = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [intensity, mode])

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0, // Behind content but above background
      }}
      aria-hidden="true"
    />
  )
}

